import { parsePhoneNumberFromString } from 'libphonenumber-js';
import countries from '@vcita/design-system/utils/countries';

/**
 * Formats a phone number in the international display style, e.g. "+1 212 555 1234".
 * Normalizes to E.164 first (prepends "+" when missing). Returns the original
 * input unchanged if it can't be parsed.
 * @param {string} rawPhoneNumber - E.164, digits, or already-formatted number
 * @returns {string} International-formatted number, or the input on failure
 */
export const formatPhoneNumberInternational = (rawPhoneNumber) => {
  if (!rawPhoneNumber) return '';
  try {
    const trimmed = String(rawPhoneNumber).trim();
    const e164 = trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/\D/g, '')}`;
    const parsed = parsePhoneNumberFromString(e164);
    return parsed ? parsed.formatInternational() : trimmed;
  } catch (e) {
    return String(rawPhoneNumber);
  }
};

/**
 * Formats a US/CA phone number into (XXX) XXX-XXXX, deriving the area code
 * from the digits. Strips a leading country code "1" when present.
 * @param {string} phoneNumber - Raw phone number (E.164 or digits)
 * @returns {string} Formatted phone number, or original input if too short
 * @example
 * formatUSPhoneNumber('18332711890') // Returns: "(833) 271-1890"
 */
export const formatUSPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';

  const digits = phoneNumber.toString().replace(/\D/g, '');
  const trimmed = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  if (trimmed.length < 10) return phoneNumber;

  return `(${trimmed.slice(0, 3)}) ${trimmed.slice(3, 6)}-${trimmed.slice(6, 10)}`;
};

// Active NANP toll-free area codes. The reserved codes (822, 880-887, 889) are not in service yet.
export const US_TOLL_FREE_AREA_CODES = ['800', '833', '844', '855', '866', '877', '888'];

/**
 * Determines whether a phone number is a US toll-free number: country code "1"
 * followed by an active toll-free area code. A "18" prefix is not enough,
 * because local area codes such as 805 or 818 also start with 8 (SUPPORT-13455).
 * @param {string} phoneNumber - Raw phone number (E.164, digits, or formatted).
 * @returns {boolean} True if the number is a US toll-free number.
 * @example
 * isUSTollFreeNumber('+18001234567') // true
 * isUSTollFreeNumber('18335551234')  // true
 * isUSTollFreeNumber('18056799233')  // false (805 is a local area code)
 */
export const isUSTollFreeNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  const digits = phoneNumber.toString().replace(/\D/g, '');
  return digits.length === 11
    && digits.startsWith('1')
    && US_TOLL_FREE_AREA_CODES.includes(digits.slice(1, 4));
};

/**
 * Formats a US phone number with an explicit area code: (XXX) XXX-XXXX.
 * Falls back to deriving the area code from the digits when none is provided.
 * @param {string} phoneNumber - The phone number to format (with or without area code).
 * @param {string} areaCode - The area code to prepend (e.g. "415").
 * @returns {string} Formatted phone number, or empty string if phoneNumber is falsy.
 */
export const formatUSPhoneNumberWithAreaCode = (phoneNumber, areaCode) => {
  if (!phoneNumber) return '';

  if (!areaCode) return formatUSPhoneNumber(phoneNumber);

  const areaCodeIndex = phoneNumber.indexOf(areaCode);
  const localNumber = areaCodeIndex !== -1
    ? phoneNumber.slice(areaCodeIndex + areaCode.length)
    : phoneNumber;

  const formattedLocal = localNumber.replace(/^(\d{3})(\d+)/, '$1-$2');
  return `(${areaCode}) ${formattedLocal}`;
};

/**
 * Formats phone number to E.164 standard
 * Removes special characters ('+', '-', '(', ')', '.', spaces)
 * @param {string} phoneNumber - Phone number string with optional country code
 * @returns {string} Clean E.164 phone number without special characters
 * @example
 * formatPhoneNumberToE164('+1-555-1234') // Returns: "15551234"
 * formatPhoneNumberToE164('(555) 123-4567') // Returns: "5551234567"
 */
export const formatPhoneNumberToE164 = (phoneNumber) => {
  if (!phoneNumber || typeof phoneNumber !== 'string') return '';
  
  return phoneNumber
    .trim()
    .replace(/[+\-().\s]/g, '');
};

/**
 * Gets the phone dial code prefix for a given ISO country code
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'IL')
 * @returns {string} Dial code without '+' (e.g., '1' for US, '972' for IL)
 */
export const getPhonePrefixByCountryCode = (countryCode) => {
  const country = countries.find((c) => c.iso2 === countryCode?.toUpperCase());
  return country?.dialCode || '1';
};

/**
 * Parses a phone number into phone number, full number, and prefix
 * Handles both phones with '+' prefix (international) and without (national)
 * @param {string} phoneNumber - Raw phone number (e.g., '+15551234567' or '5551234567')
 * @param {string} countryCode - ISO country code (e.g., 'US')
 * @returns {{ phoneNumber: string, fullNumber: string, prefix: string }} Parsed phone numbers
 * @example
 * parsePhoneNumber('+15551234567', 'US') // Returns: { phoneNumber: '5551234567', fullNumber: '15551234567', prefix: '1' }
 * parsePhoneNumber('5551234567', 'US')   // Returns: { phoneNumber: '5551234567', fullNumber: '15551234567', prefix: '1' }
 */
export const parsePhoneNumber = (rawPhoneNumber, countryCode) => {
  const prefix = getPhonePrefixByCountryCode(countryCode);
  const hasPrefix = rawPhoneNumber.includes('+');
  const formattedNumber = formatPhoneNumberToE164(rawPhoneNumber);

  return {
    phoneNumber: hasPrefix ? formattedNumber.slice(prefix.length) : formattedNumber,
    fullNumber: hasPrefix ? formattedNumber : prefix + formattedNumber,
    prefix,
  };
};
